import React, { useCallback, useContext } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase as clientSupabase } from "~/integrations/supabase/ssr.browser";
import { logger } from "~/lib/logger";

import { SupabaseSessionContext } from "./supabase-session-utils";

export const SupabaseSessionProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const queryClient = useQueryClient();
    const [isHydrated, setIsHydrated] = React.useState(false);
    const isServer = typeof window === "undefined";

    // Track hydration state
    React.useEffect(() => {
        // AIDEV-NOTE: Converted console.log to logger
        logger.info("🔄 SupabaseSessionProvider: Hydration starting");
        setIsHydrated(true);
    }, []);

    const {
        data: session,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["supabase-session"],
        queryFn: async () => {
            const {
                data: { session },
            } = await clientSupabase.auth.getSession();

            if (isServer) {
                return session;
            }

            return session;
        },
        refetchOnWindowFocus: "always",
        staleTime: 0,
        // Use hydrated data from root loader on initial load
        initialDataUpdatedAt: 0,
    });

    const isCheckingSessionStatus = React.useRef(false);
    const checkSessionStatus = useCallback(async () => {
        if (!session || isCheckingSessionStatus.current) {
            return;
        }
        try {
            isCheckingSessionStatus.current = true;
            const response = await fetch("/api/auth/session-status", {
                method: "GET",
                credentials: "include",
            });

            const unauthenticated = response.status === 401;

            const isAuthenticated =
                !unauthenticated &&
                ((await response.json()).authenticated as boolean);

            if (!isAuthenticated) {
                if (session) {
                    await clientSupabase.auth.signOut();
                    window.location.href = "/landing";
                }
                return null;
            }
        } catch (serverError) {
            logger
                .withError(serverError as Error)
                .warn(
                    "Server session validation failed; keeping client session",
                );
        } finally {
            isCheckingSessionStatus.current = false;
        }
    }, [isCheckingSessionStatus, session]);

    React.useEffect(() => {
        checkSessionStatus();
    }, [checkSessionStatus]);

    // Set up auth state listener
    useQuery({
        queryKey: ["supabase-auth-listener"],
        queryFn: async () => {
            const supabase = clientSupabase;
            const { data } = await supabase.auth.onAuthStateChange(
                (event, newSession) => {
                    if (event === "SIGNED_OUT") {
                        // Clear session data on sign out
                        queryClient.setQueryData(["supabase-session"], null);
                    } else if (newSession) {
                        // Update session data
                        queryClient.setQueryData(
                            ["supabase-session"],
                            newSession,
                        );
                    }
                },
            );

            // Return the cleanup function
            return () => {
                data.subscription.unsubscribe();
            };
        },
        // This query should only run once
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        // Only enable on client after hydration (auth listener doesn't work on server)
        enabled: !isServer && isHydrated,
    });

    // Now that we prefetch on server, provide session data from both server and client
    const value = {
        token: session?.access_token || null,
        userId: session?.user?.id || null,
        user: session?.user || null,
        session: session || null,
        loading: isLoading,
        error: (error as Error) || null,
    };

    React.useEffect(() => {
        logger
            .withMetadata({
                hasToken: !!value.token,
                hasUserId: !!value.userId,
                hasUser: !!value.user,
                hasSession: !!value.session,
                loading: value.loading,
                hasError: !!value.error,
                isServer,
                isHydrated,
            })
            .info("📤 SupabaseSessionProvider: Context value updated");
    }, [
        value.token,
        value.userId,
        value.user,
        value.session,
        value.loading,
        value.error,
        isServer,
        isHydrated,
    ]);

    return (
        <SupabaseSessionContext.Provider value={value}>
            {children}
        </SupabaseSessionContext.Provider>
    );
};

export const useSupabaseSession = () => {
    const context = useContext(SupabaseSessionContext);
    if (!context) {
        throw new Error(
            "useSupabaseSession must be used within a SupabaseSessionProvider",
        );
    }
    return context;
};
